import React, { useEffect, useState, useRef, useMemo } from 'react';
import Head from 'next/head';
import { Placeholder, LayoutServiceData, HTMLLink } from '@sitecore-jss/sitecore-jss-nextjs';
import Scripts from 'src/Scripts';
import deepEqual from 'deep-equal';
import DisclaimerModal from 'components/Modals/DisclaimerModal';
import SkipToMainContent from 'components/Generic/SkipToMainContent';
import useMetaTags from 'hooks/useMetaTags';

interface LayoutProps {
  layoutData: LayoutServiceData;
  headLinks: HTMLLink[];
}

const page_404_templateId = 'a7dfa497-6122-4679-bf99-49fffacd5fa1';

const Layout = ({ layoutData, headLinks }: LayoutProps): JSX.Element => {
  const { route } = layoutData.sitecore;
  const main = useRef();
  const [lastMainSectionBackground, setLastMainSectionBackground] = useState([]);
  const metaTags = useMetaTags();

  const themeName: string = layoutData.sitecore.context.pageTheme?.toLowerCase() || 'master';
  const pageTitle: string = layoutData.sitecore.context.metaTitle ?? 'Page';
  const metaDescription = layoutData.sitecore.context.metaDescription;
  const canonicalTag = layoutData.sitecore.context.canonicalTag;
  const notIndexPage: boolean = layoutData.sitecore.context.doNotIndex;
  const doNotIndexPage: boolean = useMemo(() => {
    return (
      layoutData.sitecore.context.language?.toLowerCase() === 'en' ||
      layoutData.sitecore.route?.templateId === page_404_templateId ||
      notIndexPage
    );
  }, [notIndexPage]);
  const pathToIcon = themeName === 'financialfairness' ? '/financialfairness' : '';

  useEffect(() => {
    /* eslint-disable */
    const pageThemeColors: Record<string, string> =
      require(`./assets/styles/themes/${themeName}.ts`).default;
    for (const [key, value] of Object.entries(pageThemeColors)) {
      document.documentElement.style.setProperty(key, value);
    }

    if (!main.current || !main.current.children.length) return;

    const children = main.current.children;
    const lastChild = children[children.length - 1];

    if (!lastChild) return;

    const classList = Array.from(lastChild.classList);
    const filteredClasses = classList.filter(
      (className) =>
        className.includes('vectorBasedBackground') ||
        className.includes('feature-banner-section') ||
        className.includes('background-')
    );

    const transformedClasses = filteredClasses.map((className) => `main-ls-bg-${className}`);

    setLastMainSectionBackground(transformedClasses);
  }, []);

  return (
    <>
      <Scripts />
      <Head>
        {metaTags.metaTags.map((tag) => (
          <meta
            {...(tag.name ? { name: tag.name } : {})}
            property={tag.property}
            content={tag.content}
            data-vmid={tag.vmid}
          />
        ))}
        {metaTags.langVersions?.map((tag) => (
          <link hreflang={tag.hreflang} href={tag.href} rel={tag.rel} data-vmid={tag.vmid} />
        ))}
        {canonicalTag?.href && (
          <link data-vmid="canonical" rel="canonical" href={canonicalTag?.href} />
        )}
        {canonicalTag?.href && (
          <meta vmid="og:url" property="og:url" content={canonicalTag?.href} />
        )}
        <meta name="robots" content={doNotIndexPage ? 'noindex' : 'index'} data-id="metaIndexTag" />
        <title>{pageTitle}</title>
        <link rel="icon" href={`${pathToIcon}/favicon.ico`} sizes="32x32" />
        <link rel="shortcut icon" href={`${pathToIcon}/favicon.ico`} sizes="32x32" />
        <link rel="icon" href={`${pathToIcon}/icon.svg`} type="image/svg+xml" />
        <link rel="apple-touch-icon" href={`${pathToIcon}/apple-touch-icon.png`} />
        <link rel="manifest" href={`/webmanifest.json`} />
        {headLinks.map((headLink) => (
          <link rel={headLink.rel} key={headLink.href} href={headLink.href} />
        ))}
        {metaDescription && <meta name="description" content={`${metaDescription}`} />}
      </Head>

      {/*<Navigation />*/}
      {/* root placeholder for the app, which we add components to using route data */}
      {route && (
        <div className={`layout-wrapper ${themeName} ${lastMainSectionBackground}`}>
          <SkipToMainContent />
          <Placeholder name="abrdn-header" rendering={route} />
          <div id="main-content" ref={main}>
            <Placeholder
              name="abrdn-main"
              rendering={route}
              context={layoutData.sitecore.context}
            />
          </div>
          <Placeholder name="abrdn-footer" rendering={route} />
          <DisclaimerModal layoutData={layoutData} />
        </div>
      )}
    </>
  );
};

const propsAreEqual = (prevProps: LayoutProps, nextProps: LayoutProps) => {
  if (deepEqual(prevProps.layoutData.sitecore.route, nextProps.layoutData.sitecore.route)) {
    return true;
  }

  return false;
};

export default React.memo(Layout, propsAreEqual);
